import { createContext, useContext, useState, useCallback } from 'react';
import { Product } from '../types';

interface ChatBotContextType {
  sendMessage: (message: string) => Promise<void>;
  messages: Message[];
  isLoading: boolean;
  quickReplies: string[];
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  type?: 'text' | 'product' | 'quickReplies';
  data?: any;
}

const ChatBotContext = createContext<ChatBotContextType | undefined>(undefined);

export const useChatBot = () => {
  const context = useContext(ChatBotContext);
  if (!context) {
    throw new Error('useChatBot must be used within a ChatBotProvider');
  }
  return context;
};

const generateUniqueId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const ChatBotProvider = ({ children }: { children: React.ReactNode }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: generateUniqueId(),
      text: "Hello! I'm your personal jewelry assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const quickReplies = [
    "Show me rings",
    "Shipping info",
    "Size guide",
    "Custom orders",
    "Track my order",
    "Gift suggestions"
  ];

  const generateResponse = useCallback(async (userMessage: string): Promise<Message> => {
    // This is where you would integrate with your AI backend
    // For now, we'll use a simple response system
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes('ring')) {
      return {
        id: generateUniqueId(),
        text: "I'd be happy to show you our ring collection. We have a variety of styles from classic solitaires to modern designs. What type of ring are you interested in?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    if (lowerMessage.includes('shipping')) {
      return {
        id: generateUniqueId(),
        text: "We offer free shipping on all orders over $500. Standard shipping takes 3-5 business days, while express shipping (1-2 business days) is available for an additional fee.",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    if (lowerMessage.includes('size')) {
      return {
        id: generateUniqueId(),
        text: "To find your ring size, you can visit any local jeweler for a professional measurement, or use our printable ring sizer available on our website. Would you like me to guide you through the process?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    if (lowerMessage.includes('custom')) {
      return {
        id: generateUniqueId(),
        text: "Our custom jewelry service allows you to create a unique piece that's perfect for you. We'll work with you through the entire process, from design to final creation. Would you like to schedule a consultation?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    if (lowerMessage.includes('track')) {
      return {
        id: generateUniqueId(),
        text: "I can help you track your order. Could you please provide your order number?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    if (lowerMessage.includes('gift')) {
      return {
        id: generateUniqueId(),
        text: "I'd be happy to help you find the perfect gift! Could you tell me more about the occasion and the recipient's style preferences?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
    }

    return {
      id: generateUniqueId(),
      text: "I'm here to help you with any questions about our jewelry, orders, or services. Feel free to ask about specific pieces, shipping, sizing, or custom orders.",
      sender: 'bot',
      timestamp: new Date(),
      type: 'text'
    };
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    setIsLoading(true);
    
    // Add user message
    const userMessage: Message = {
      id: generateUniqueId(),
      text: message,
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };
    
    setMessages(prev => [...prev, userMessage]);

    try {
      // Generate and add bot response
      const botResponse = await generateResponse(message);
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error generating response:', error);
      setMessages(prev => [...prev, {
        id: generateUniqueId(),
        text: "I apologize, but I'm having trouble processing your request right now. Please try again later.",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [generateResponse]);

  return (
    <ChatBotContext.Provider value={{ sendMessage, messages, isLoading, quickReplies }}>
      {children}
    </ChatBotContext.Provider>
  );
}; 